import { Link as RouterLink } from 'react-router';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Autocomplete,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import invitationService from './invitationService';
import roleCatalogService from '../roleCatalog/roleCatalogService';
import tenantService from '../tenants/tenantService';
import useCheckPermission from '../../hooks/useCheckPermission';
import DataList from '../../components/DataList';

const InvitationsPage = () => {
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar } = useSnackbar();
  const { canPlatform, canTenant } = useCheckPermission();
  const canPlatformInvite = canPlatform([
    'ManageTenantUsers',
    'ManageTenants',
  ]);
  const canTenantInvite = canTenant(['ManageAuthorization']);
  const canManage = canPlatformInvite || canTenantInvite;
  const activeTenantId = useSelector(
    (state: any) => state.user.userInfo?.activeTenantId
  );
  const [rows, setRows] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [email, setEmail] = useState('');
  const [tenantId, setTenantId] = useState(activeTenantId || '');
  const [roleTemplateId, setRoleTemplateId] = useState<string | null>(null);
  const [createdCode, setCreatedCode] = useState<string | null>(null);

  const load = (id?: string) => {
    const target = id || tenantId || activeTenantId;
    // API requires TenantId — do not call list without one (avoids 400 → grid crash).
    if (!target) {
      setRows([]);
      return;
    }
    dispatch({ type: 'SHOW_QUERY' });
    invitationService
      .list(target)
      .then((response) => {
        if (!response.hasError) setRows(response.records ?? []);
        else {
          setRows([]);
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  useEffect(() => {
    if (canPlatform(['ManageTenants'])) {
      tenantService.list().then((response) => {
        if (!response.hasError) setTenants(response.records);
      });
    }
    roleCatalogService.listTemplates().then((response) => {
      if (!response.hasError) setTemplates(response.records);
    });
    if (activeTenantId && !tenantId) {
      setTenantId(activeTenantId);
    }
    load(activeTenantId);
  }, []);

  useEffect(() => {
    if (tenantId) load(tenantId);
  }, [tenantId]);

  const createInvitation = () => {
    dispatch({ type: 'SHOW_SAVE' });
    invitationService
      .create({
        email,
        tenantId: tenantId || undefined,
        targetRoleTemplateId: roleTemplateId,
      })
      .then((response) => {
        if (response.hasError) {
          enqueueSnackbar((response.errorMessages ?? []).join(','), {
            variant: 'error',
          });
        } else {
          const code = response.result?.code ?? response.result?.Code;
          setCreatedCode(code);
          enqueueSnackbar('Invitation created', { variant: 'success' });
          setEmail('');
          load();
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant="h5">Bérlő meghívás (platform)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Meghívó bármelyik bérlőbe SaaS supportból. Az aktív bérlő saját
            meghívói a Felhasználók oldalon vannak.
          </Typography>
        </Grid>
        {createdCode && (
          <Grid size={12}>
            <Alert severity="info">
              Invitation code: <strong>{createdCode}</strong> — share with the
              invitee (accept at /invitations/accept).
            </Alert>
          </Grid>
        )}
        {canManage && (
          <>
            {canPlatform(['ManageTenants']) && (
              <Grid size={{ xs: 12, md: 4 }}>
                <Autocomplete
                  options={tenants}
                  getOptionLabel={(o: any) => o.name ?? ''}
                  value={tenants.find((t) => t.id === tenantId) ?? null}
                  onChange={(_e, v: any) => setTenantId(v?.id ?? '')}
                  renderInput={(params) => (
                    <TextField {...params} label="Bérlő" />
                  )}
                />
              </Grid>
            )}
            {!tenantId && canPlatform(['ManageTenants']) && (
              <Grid size={12}>
                <Alert severity="info">
                  Válassz bérlőt a meghívók listázásához és létrehozásához.
                </Alert>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Autocomplete
                options={templates}
                getOptionLabel={(o: any) => o.name ?? ''}
                value={templates.find((t) => t.id === roleTemplateId) ?? null}
                onChange={(_e, v: any) => setRoleTemplateId(v?.id ?? null)}
                renderInput={(params) => (
                  <TextField {...params} label="Bérlő szerepkör" />
                )}
              />
            </Grid>
            <Grid size={12}>
              <Button
                variant="contained"
                disabled={!email || !tenantId}
                onClick={createInvitation}
              >
                Create invitation
              </Button>
            </Grid>
          </>
        )}
        <Grid size={12}>
          <DataList
            rows={rows}
            columns={[
              { field: 'email', headerName: 'Email', flex: 120 },
              { field: 'code', headerName: 'Code', flex: 120 },
              { field: 'expiresAt', headerName: 'Expires', flex: 100 },
              { field: 'acceptedAt', headerName: 'Accepted', flex: 100 },
            ]}
          />
        </Grid>
        <Grid size={12}>
          <Box>
            <Button
              component={RouterLink}
              to="/invitations/accept"
              variant="outlined"
            >
              Accept invitation
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default InvitationsPage;
